import { useState } from 'react';
import { Loader2, Search, Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { matchingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// Reconciliation Pointer Heal (Diagnostics card): evidence-driven repair of
// the pointer-without-flag anomaly (routes/admin_reconciliation_pointer_
// heal.py -- CLAUDE.md prompt: INV-00359 / BROS-INV-00267 report). Preview
// is read-only and buckets every candidate transaction into healable
// (restorable, with the evidence_classes that corroborate it),
// ambiguous (multiple live pointers, ownership unclear), and unverified
// (no corroborating evidence) -- the latter two are "reported, never
// touched" per the module's own discipline (CLAUDE.md section 6: don't
// auto-correct evidence). Apply is gated behind a Preview run and only
// ever acts on the healable bucket.
function evidenceLabel(row) {
  const classes = row.evidence_classes && row.evidence_classes.length ? row.evidence_classes : [row.reason];
  return classes.filter(Boolean).join(', ');
}

function pointerLabel(row) {
  return `${row.pointer_field}: ${row.pointer_value ?? '-'}`;
}

export function ReconciliationPointerHealPanel() {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const runPreview = async () => {
    setPreviewing(true);
    setApplyResult(null);
    try {
      const { data } = await matchingAPI.previewReconciliationPointerHeal();
      setPreview(data.pointer_heal);
      const healableCount = data.pointer_heal?.healable_count ?? 0;
      const reportedCount = (data.pointer_heal?.ambiguous_count ?? 0) + (data.pointer_heal?.unverified_count ?? 0);
      if (healableCount > 0 || reportedCount > 0) {
        toast.warning(`${healableCount} healable, ${reportedCount} reported (not touched)`);
      } else {
        toast.success('No pointer-without-flag transactions found');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await matchingAPI.applyReconciliationPointerHeal();
      setApplyResult(data.pointer_heal);
      toast.success(`${data.pointer_heal?.healed_count ?? 0} healed, ${data.pointer_heal?.failed_count ?? 0} failed`);
      const { data: refreshed } = await matchingAPI.previewReconciliationPointerHeal();
      setPreview(refreshed.pointer_heal);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const healable = preview?.healable ?? [];
  const ambiguous = preview?.ambiguous ?? [];
  const unverified = preview?.unverified ?? [];
  const healableCount = preview?.healable_count ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Wrench className="h-4 w-4 text-amber-500" />
          Reconciliation Pointer Heal
        </CardTitle>
        <CardDescription>
          Repairs transactions with a live reconciliation pointer but is_reconciled already
          false (INV-00359 / BROS-INV-00267 report). Evidence-driven: a row is only healed if
          its pointer is corroborated by independent evidence (evidence_classes below) --
          ambiguous (multiple live pointers) and unverified (no evidence) rows are reported,
          never touched.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button data-testid="pointer-heal-preview" size="sm" variant="outline" onClick={runPreview} disabled={previewing}>
            {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
            Preview
          </Button>
          <Button
            data-testid="pointer-heal-apply"
            size="sm"
            onClick={runApply}
            disabled={applying || !preview || healableCount === 0}
          >
            {applying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wrench className="h-4 w-4 mr-1" />}
            Apply {healableCount ? `${healableCount} transaction(s)` : ''}
          </Button>
        </div>

        {preview && (
          <div className="space-y-4" data-testid="pointer-heal-result">
            <p className="text-sm text-muted-foreground">
              {healableCount} healable, {preview.ambiguous_count ?? 0} ambiguous, {preview.unverified_count ?? 0} unverified
            </p>

            <div className="space-y-1">
              <p className="text-xs font-medium">Healable (pointer restored on Apply)</p>
              {healable.length === 0 ? (
                <div className="text-xs text-muted-foreground" data-testid="pointer-heal-healable-empty">
                  No healable transactions.
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg" data-testid="pointer-heal-healable">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Transaction ID</th>
                        <th className="text-left px-3 py-2 font-medium">Invoice / Pointer</th>
                        <th className="text-left px-3 py-2 font-medium">Proposed Restore</th>
                        <th className="text-left px-3 py-2 font-medium">Evidence Classes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {healable.map((row) => (
                        <tr key={row.transaction_id} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{row.transaction_id}</td>
                          <td className="px-3 py-2 font-mono text-xs">{pointerLabel(row)}</td>
                          <td className="px-3 py-2 text-xs">is_reconciled: false &rarr; true</td>
                          <td className="px-3 py-2 text-xs" data-testid={`pointer-heal-evidence-${row.transaction_id}`}>
                            {evidenceLabel(row)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                Reported, not touched -- ambiguous (multiple live pointers)
              </p>
              {ambiguous.length === 0 ? (
                <div className="text-xs text-muted-foreground" data-testid="pointer-heal-ambiguous-empty">
                  No ambiguous transactions.
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg border-amber-200 dark:border-amber-800" data-testid="pointer-heal-ambiguous">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Transaction ID</th>
                        <th className="text-left px-3 py-2 font-medium">Live Pointer Fields</th>
                        <th className="text-left px-3 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ambiguous.map((row) => (
                        <tr key={row.transaction_id} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{row.transaction_id}</td>
                          <td className="px-3 py-2 font-mono text-xs">{(row.live_pointer_fields || []).join(', ')}</td>
                          <td className="px-3 py-2 text-xs">{row.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                Reported, not touched -- unverified (no corroborating evidence)
              </p>
              {unverified.length === 0 ? (
                <div className="text-xs text-muted-foreground" data-testid="pointer-heal-unverified-empty">
                  No unverified transactions.
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg border-amber-200 dark:border-amber-800" data-testid="pointer-heal-unverified">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Transaction ID</th>
                        <th className="text-left px-3 py-2 font-medium">Invoice / Pointer</th>
                        <th className="text-left px-3 py-2 font-medium">Evidence Classes</th>
                        <th className="text-left px-3 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unverified.map((row) => (
                        <tr key={row.transaction_id} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{row.transaction_id}</td>
                          <td className="px-3 py-2 font-mono text-xs">{pointerLabel(row)}</td>
                          <td className="px-3 py-2 text-xs">{(row.evidence_classes || []).join(', ') || '-'}</td>
                          <td className="px-3 py-2 text-xs">{row.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {applyResult && (
          <div className="space-y-2" data-testid="pointer-heal-apply-result">
            <p className="text-xs text-muted-foreground">
              Last apply: {applyResult.healed_count ?? 0} healed
              {applyResult.failed_count > 0 && `, ${applyResult.failed_count} failed`}
            </p>
            {applyResult.failed_count > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-red-600 dark:text-red-400">
                  Failed on apply too -- the candidate no longer matched at write time (a
                  concurrent change), not a batch-abort.
                </p>
                <div className="overflow-x-auto border rounded-lg border-red-200 dark:border-red-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Transaction ID</th>
                        <th className="text-left px-3 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(applyResult.failed || []).map((f) => (
                        <tr key={f.transaction_id} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{f.transaction_id}</td>
                          <td className="px-3 py-2 text-xs">{f.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
